/**
 * Structured event capture — make the high-signal fields first-class instead of trusting free
 * text + AI extraction to infer them. (Port of lib/event_intake.py.)
 *
 * The engine leans hard on four fields most people never state in prose: how precise the DATE is
 * (date_accuracy_days — a date-only event resolves to ~tens of minutes, an exact clock time pins the
 * second), whether an exact TIME is known (event_time → the KP Ruling Planets and prana level), and
 * WHERE the event happened (event_lat/lon → the transit/Ruling-Planet charts). `build_structured_event`
 * turns explicit form inputs into the canonical event dict the scorer consumes, so these are captured
 * deliberately rather than guessed.
 *
 * Geocoding is intentionally a pluggable seam (`set_geocoder` / `geocode_place`): the choice of
 * provider — offline city table, Nominatim/geopy, a paid API — is the app owner's call. Until one is
 * wired, the structured form simply asks for latitude/longitude directly.
 *
 * DIVERGENCE from Python: the geocoder seam is ASYNC (the browser default provider uses fetch),
 * so `geocode_place` and `build_structured_event` return Promises. Synchronous geocoder functions
 * are still accepted (their plain return value is awaited transparently).
 */

import { PlainDate, compareDates, formatISODate, parseISODate, todayLocal } from "./datetime";
import { EVENT_CATEGORIES } from "./config";
import type { EventRecord } from "../types";

// Date-precision label -> date_accuracy_days (the ± half-width of the date uncertainty, in days).
// event_payload_to_life_event reads this directly: 0 means the calendar day is certain; larger
// values widen the dasha window the event is allowed to match and down-weight the event.
export const DATE_PRECISION_DAYS: Record<string, number> = {
  exact_day: 0.0,
  within_few_days: 3.0,
  within_week: 7.0,
  within_month: 15.0,
  within_quarter: 45.0,
  within_year: 182.0,
};
export const DEFAULT_DATE_PRECISION = "exact_day";

const _POLARITY = new Set(["positive", "negative", "neutral"]);

export type Geocoder = (name: string) => Promise<[number, number] | null> | [number, number] | null;

// Pluggable geocoder: name -> (lat, lon) or None. Default unset; the app owner wires a provider.
let _GEOCODER: Geocoder | null = null;

/** Install (or clear) the place-name -> (lat, lon) resolver. Provider choice is the owner's. */
export function set_geocoder(fn: Geocoder | null): void {
  _GEOCODER = fn;
}

/** Resolve a place name to (lat, lon) via the installed geocoder, or None if unresolved/unset. */
export async function geocode_place(name: string | null | undefined): Promise<[number, number] | null> {
  if (!name || _GEOCODER === null) return null;
  let result: [number, number] | null;
  try {
    result = await _GEOCODER(String(name));
  } catch {
    return null;
  }
  if (!result) return null;
  const lat = Number(result[0]);
  const lon = Number(result[1]);
  return [lat, lon];
}

/** Map a precision label (or an explicit custom ± in days) to date_accuracy_days. */
export function date_accuracy_days_for(precision: string | null | undefined, custom_days: number | null = null): number {
  if (custom_days !== null && custom_days !== undefined) {
    return Math.max(0.0, Number(custom_days));
  }
  const key = String(precision || DEFAULT_DATE_PRECISION).trim().toLowerCase();
  return DATE_PRECISION_DAYS[key] ?? 0.0;
}

// Raises if not ISO YYYY-MM-DD — fail loudly rather than silently mis-scoring.
function _valid_iso_date(value: string): string {
  return formatISODate(parseISODate(String(value)));
}

/**
 * Why the event's dates are impossible, or None if they are fine.
 *
 * Two classes of garbage the scorer would otherwise swallow silently: an event dated
 * before (or on) the birth date produces a meaningless pre-birth dasha and is dropped
 * deep in the scan with no feedback; a FUTURE event hasn't happened, yet would be scored
 * as though it had and pull the rectified time toward fiction. Returned as a message
 * (not raised) so the AI-extraction path can skip-and-log while forms can show it.
 */
export function event_date_problem(
  start_date: string,
  options: { birth_date?: string | null; end_date?: string | null; today?: PlainDate | null } = {},
): string | null {
  const { birth_date = null, end_date = null, today = null } = options;
  let start: PlainDate;
  try {
    start = parseISODate(String(start_date));
  } catch {
    return `Event date '${start_date}' is not a valid YYYY-MM-DD date.`;
  }
  const limit = today || todayLocal();
  if (compareDates(start, limit) > 0) {
    return `Event date ${formatISODate(start)} is in the future — only events that have already happened can date a birth time.`;
  }
  if (end_date) {
    try {
      if (compareDates(parseISODate(String(end_date)), limit) > 0) {
        return `Event end date ${end_date} is in the future — only events that have already happened can date a birth time.`;
      }
    } catch {
      return `Event end date '${end_date}' is not a valid YYYY-MM-DD date.`;
    }
  }
  if (birth_date) {
    let born: PlainDate | null;
    try {
      born = parseISODate(String(birth_date));
    } catch {
      born = null;
    }
    if (born !== null && compareDates(start, born) <= 0) {
      return (
        `Event date ${formatISODate(start)} is on or before the birth date ${formatISODate(born)} — check for a typo ` +
        "in the event year."
      );
    }
  }
  return null;
}

export interface BuildStructuredEventArgs {
  start_date: string;
  category: string;
  label?: string;
  end_date?: string | null;
  date_precision?: string;
  custom_accuracy_days?: number | null;
  event_time?: string | null;
  place_name?: string | null;
  event_lat?: number | null;
  event_lon?: number | null;
  polarity?: string;
  severity?: number;
  confidence?: number;
  weight?: number;
  source?: string;
  birth_date?: string | null;
}

/**
 * Build a canonical event dict from explicit structured inputs, validating and deriving the
 * high-signal fields. Raises (rejects) on a bad category, unparseable date, a future date, or
 * (when birth_date is given) a pre-birth date — so the caller can surface the problem instead
 * of scoring garbage.
 *
 * Location precedence: explicit lat/lon win; otherwise the place name is geocoded if a provider
 * is installed. event_time is kept only when an exact time is genuinely known.
 *
 * DIVERGENCE from Python: async, because geocoding may hit the network in the browser.
 */
export async function build_structured_event(args: BuildStructuredEventArgs): Promise<EventRecord> {
  const {
    start_date,
    category,
    label = "",
    end_date = null,
    date_precision = DEFAULT_DATE_PRECISION,
    custom_accuracy_days = null,
    event_time = null,
    place_name = null,
    event_lat = null,
    event_lon = null,
    polarity = "positive",
    severity = 3.0,
    confidence = 1.0,
    weight = 1.0,
    source = "manual",
    birth_date = null,
  } = args;

  const cat = String(category || "").trim().toLowerCase();
  if (!EVENT_CATEGORIES.includes(cat)) {
    throw new Error(`Unknown category '${category}'; must be one of ${JSON.stringify(EVENT_CATEGORIES)}.`);
  }
  let start = _valid_iso_date(start_date);
  let end = end_date ? _valid_iso_date(end_date) : start;
  if (end < start) {
    [start, end] = [end, start];
  }
  const problem = event_date_problem(start, { birth_date, end_date: end });
  if (problem) {
    throw new Error(problem);
  }

  let pol = String(polarity || "positive").trim().toLowerCase();
  if (!_POLARITY.has(pol)) {
    pol = "positive";
  }

  let lat = event_lat;
  let lon = event_lon;
  if ((lat === null || lat === undefined || lon === null || lon === undefined) && place_name) {
    const resolved = await geocode_place(place_name);
    if (resolved !== null) {
      [lat, lon] = resolved;
    }
  }

  const time_clean = event_time ? String(event_time).trim() : "";
  const event: EventRecord = {
    start_date: start,
    end_date: end,
    category: cat,
    label: String(label || "").trim().slice(0, 180) || `${cat} on ${start}`,
    polarity: pol,
    severity: Math.max(0.0, Math.min(5.0, Number(severity))),
    confidence: Math.max(0.0, Math.min(1.0, Number(confidence))),
    weight: Math.max(0.0, Number(weight)),
    date_accuracy_days: date_accuracy_days_for(date_precision, custom_accuracy_days),
    source,
  };
  if (time_clean) {
    event["event_time"] = time_clean;
  }
  if (place_name) {
    event["place_name"] = String(place_name).trim().slice(0, 120);
  }
  if (lat !== null && lat !== undefined && lon !== null && lon !== undefined) {
    event["event_lat"] = Number(lat);
    event["event_lon"] = Number(lon);
  }
  return event;
}
